import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import AddCommentIcon from '@mui/icons-material/AddComment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BugReportIcon from '@mui/icons-material/BugReport';
import ChatIcon from '@mui/icons-material/Chat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import DOMPurify from 'dompurify';
import { GetAIThread, GetAIThreads, StreamAIQuestion } from '../../REST/ai';
import AIObservability from './AIObservability';
import AIQueryResults, { messageToolCalls } from './AIQueryResults';
import PageContent from '../../themes/components/PageContent';
import PageHeader from '../../themes/components/PageHeader';
import PageShell from '../../themes/components/PageShell';

const suggestions = [
    'Help me define an overdue invoice report.',
    'What filters should a property occupancy report include?',
    'Help me describe a monthly workorder summary.',
];

const THREAD_CACHE_TTL_MS = 30_000;

const localMessage = (role, content, extra = {}) => ({
    message_id: `${role}-${Date.now()}-${Math.random()}`,
    role,
    content,
    ...extra,
});

const escapeHtml = (text) => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Minimal, safe markdown -> HTML (bold, italic, inline code, code blocks, links,
// headings, lists). Output is sanitized with DOMPurify before rendering.
const renderMarkdown = (raw) => {
    const source = String(raw || '');
    const blocks = source.split(/```/);
    const html = blocks.map((block, index) => {
        if (index % 2 === 1)
            return `<pre class="ai-md-pre"><code>${escapeHtml(block.replace(/^\w*\n/, ''))}</code></pre>`;
        const lines = escapeHtml(block).split('\n');
        const out = [];
        let listOpen = false;
        const closeList = () => { if (listOpen) { out.push('</ul>'); listOpen = false; } };
        lines.forEach((line) => {
            const heading = line.match(/^(#{1,4})\s+(.*)$/);
            const bullet = line.match(/^\s*[-*]\s+(.*)$/);
            if (heading) {
                closeList();
                const level = Math.min(heading[1].length + 2, 6);
                out.push(`<h${level}>${heading[2]}</h${level}>`);
            }
            else if (bullet) {
                if (!listOpen) { out.push('<ul>'); listOpen = true; }
                out.push(`<li>${bullet[1]}</li>`);
            }
            else if (line.trim() === '') {
                closeList();
                out.push('<br/>');
            }
            else {
                closeList();
                out.push(`<p>${line}</p>`);
            }
        });
        closeList();
        return out.join('');
    }).join('');

    return html
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
};

const Markdown = React.memo(function Markdown({ content }) {
    const safe = DOMPurify.sanitize(renderMarkdown(content), { ADD_ATTR: ['target', 'rel'] });
    return (
        <Box
            sx={{
                fontSize: 14,
                lineHeight: 1.65,
                '& p': { m: 0, mb: 0.5 },
                '& h3, & h4, & h5, & h6': { mt: 1, mb: 0.5, fontWeight: 600 },
                '& ul': { my: 0.5, pl: 2.5 },
                '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 12, fontFamily: 'monospace' },
                '& .ai-md-pre': { bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, overflow: 'auto', fontSize: 12 },
                '& .ai-md-pre code': { bgcolor: 'transparent', p: 0, color: 'inherit' },
                '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: safe }}
        />
    );
});

const ClarificationPanel = React.memo(function ClarificationPanel({ slots, onSelect, disabled }) {
    return (
        <Alert
            severity="warning"
            icon={<HelpOutlineIcon />}
            sx={{ width: '100%', mt: 1, '& .MuiAlert-message': { width: '100%' } }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Clarification needed</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
                {slots.map((slot, index) => (
                    <Box key={`${slot.slot || 'slot'}-${index}`}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {slot.question || `Which ${slot.slot}?`}
                        </Typography>
                        {(slot.candidates || []).length > 0 && (
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                {slot.candidates.map((candidate) => (
                                    <Chip
                                        key={candidate}
                                        label={candidate}
                                        color="warning"
                                        variant="outlined"
                                        clickable={!disabled}
                                        disabled={disabled}
                                        onClick={() => !disabled && onSelect(candidate)}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                ))}
            </Stack>
        </Alert>
    );
});

// Shown once a query has been produced, to keep each conversation grounded to a
// single intent: continue refining it, start a fresh one, or end it. Mirrors the
// reference app's BranchCompletionPrompt.
const CompletionPrompt = React.memo(function CompletionPrompt({ onContinue, onNew, onEnd, disabled }) {
    return (
        <Paper
            elevation={0}
            sx={{
                mx: 'auto',
                my: 1.5,
                px: 2.5,
                py: 2,
                width: '100%',
                maxWidth: 520,
                textAlign: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>This query is complete.</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Refine this same request, or start a new conversation for a different question.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Button variant="contained" size="small" disabled={disabled} onClick={onContinue}>
                    Continue refining
                </Button>
                <Button variant="outlined" size="small" disabled={disabled} startIcon={<AddCommentIcon />} onClick={onNew}>
                    New conversation
                </Button>
                <Button variant="text" size="small" color="error" disabled={disabled} onClick={onEnd}>
                    End conversation
                </Button>
            </Stack>
        </Paper>
    );
});

// Model-offered next-action buttons. Clicking one injects the suggestion's full
// message as a user turn (no typing needed).
const SuggestionButtons = React.memo(function SuggestionButtons({ suggestions, onSuggest, disabled }) {
    return (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {suggestions.map((suggestion, index) => (
                <Button
                    key={`${suggestion.label}-${index}`}
                    size="small"
                    variant="outlined"
                    disabled={disabled}
                    onClick={() => !disabled && onSuggest(suggestion.message)}
                    title={suggestion.message}
                    sx={{ textTransform: 'none', borderRadius: 5 }}
                >
                    {suggestion.label}
                </Button>
            ))}
        </Stack>
    );
});

const MessageBubble = React.memo(function MessageBubble({ message, onClarify, onSuggest, actionsDisabled }) {
    const isUser = message.role === 'user';
    const toolCalls = messageToolCalls(message);
    const clarification = message.clarification || [];
    const injected = !!message.injected;
    const suggestions = message.suggestions || message.metadata?.extra?.suggestions || [];
    return (
        <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
            <Paper
                variant={isUser && !injected ? 'elevation' : 'outlined'}
                elevation={isUser && !injected ? 0 : undefined}
                sx={{
                    width: isUser ? 'auto' : '100%',
                    maxWidth: isUser ? { xs: '92%', md: '78%' } : '100%',
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    boxShadow: isUser && !injected ? 2 : 'none',
                    // Button-injected user turns are shown desaturated with a dashed
                    // outline to signal they came from a suggestion, not typing.
                    ...(injected
                        ? {
                            border: '1.5px dashed',
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            color: 'text.primary',
                        }
                        : {
                            bgcolor: isUser ? 'primary.main' : 'background.paper',
                            color: isUser ? 'primary.contrastText' : 'text.primary',
                        }),
                }}
            >
                {message.loading && !message.content ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} />
                        <Typography variant="body2" color="text.secondary">Thinking...</Typography>
                    </Stack>
                ) : isUser ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                        {message.content}
                    </Typography>
                ) : (
                    message.content && <Markdown content={message.content} />
                )}
                {message.error && (
                    <Alert severity="error" sx={{ mt: message.content ? 1 : 0 }}>{message.error}</Alert>
                )}
                {clarification.length > 0 && (
                    <ClarificationPanel slots={clarification} onSelect={onClarify} disabled={actionsDisabled} />
                )}
                {!isUser && toolCalls.length > 0 && (
                    <Box sx={{ mt: message.content ? 2 : 0 }}>
                        <AIQueryResults toolCalls={toolCalls} />
                    </Box>
                )}
                {!isUser && suggestions.length > 0 && (
                    <SuggestionButtons suggestions={suggestions} onSuggest={onSuggest} disabled={actionsDisabled} />
                )}
            </Paper>
        </Box>
    );
});

export default function AIReporting() {
    const [view, setView] = useState('chat');
    const [threads, setThreads] = useState([]);
    const [activeThreadId, setActiveThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [traces, setTraces] = useState([]);
    const [observabilityRefreshKey, setObservabilityRefreshKey] = useState(0);
    // Conversation lifecycle: 'active' (can send) | 'choice' (a query completed,
    // user picks continue/new/end) | 'ended' (locked). Keeps each conversation
    // grounded to one intent instead of drifting.
    const [phase, setPhase] = useState('active');
    const scrollRef = useRef(null);
    const abortRef = useRef(null);
    const activeThreadRef = useRef(null);
    const threadCacheRef = useRef(new Map());
    const producedQueryRef = useRef(false);

    const loadThreads = useCallback(async () => {
        const response = await GetAIThreads();
        setThreads(response.data || []);
    }, []);

    const refreshThread = useCallback(async (threadId) => {
        const response = await GetAIThread(threadId);
        const nextMessages = response.data?.messages || [];
        threadCacheRef.current.set(threadId, {
            messages: nextMessages,
            loadedAt: Date.now(),
        });
        if (activeThreadRef.current === threadId)
            setMessages(nextMessages);
        return nextMessages;
    }, []);

    const openThread = useCallback(async (threadId) => {
        setError(null);
        setPhase('active');
        activeThreadRef.current = threadId;
        setActiveThreadId(threadId);
        const cachedThread = threadCacheRef.current.get(threadId);
        if (cachedThread)
            setMessages(cachedThread.messages);
        if (cachedThread && Date.now() - cachedThread.loadedAt < THREAD_CACHE_TTL_MS)
            return cachedThread.messages;
        await refreshThread(threadId);
    }, [refreshThread]);

    useEffect(() => {
        loadThreads().catch((err) => setError(err.message || 'Could not load conversations'));
        return () => abortRef.current?.abort();
    }, [loadThreads]);

    useEffect(() => {
        if (scrollRef.current)
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, status]);

    const newConversation = () => {
        abortRef.current?.abort();
        activeThreadRef.current = null;
        setActiveThreadId(null);
        setMessages([]);
        setDraft('');
        setStatus(null);
        setError(null);
        setPhase('active');
    };

    const sendQuestion = async (question = draft, opts = {}) => {
        const normalized = question.trim();
        if (normalized.length < 3 || sending)
            return;
        const injected = !!opts.injected;

        const assistantId = `assistant-${Date.now()}`;
        const traceId = `trace-${Date.now()}-${Math.random()}`;
        let resolvedThreadId = activeThreadId;
        let streamError = null;
        setDraft('');
        setError(null);
        setSending(true);
        setStatus('Thinking');
        setPhase('active');
        producedQueryRef.current = false;
        setTraces((current) => [
            {
                trace_id: traceId,
                request_id: null,
                thread_id: activeThreadId,
                question: normalized,
                started_at: new Date().toISOString(),
                status: 'running',
                stage: 'starting',
                context: null,
                tool_calls: [],
                answer: '',
                error: null,
            },
            ...current,
        ].slice(0, 25));
        setMessages((current) => [
            ...current,
            localMessage('user', normalized, { injected }),
            {
                ...localMessage('assistant', ''),
                message_id: assistantId,
                loading: true,
                tool_calls: [],
            },
        ]);

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            await StreamAIQuestion(
                { question: normalized, thread_id: activeThreadId },
                (event, data) => {
                    setTraces((current) => current.map((trace) => {
                        if (trace.trace_id !== traceId)
                            return trace;
                        if (event === 'thread')
                            return { ...trace, request_id: data.request_id, thread_id: data.thread_id };
                        if (event === 'status')
                            return { ...trace, stage: data.stage || trace.stage };
                        if (event === 'context')
                            return { ...trace, context: data, stage: data.error ? 'context failed' : 'context selected' };
                        if (event === 'tool_call') {
                            return {
                                ...trace,
                                stage: `running ${data.name || 'tool'}`,
                                tool_calls: [
                                    ...trace.tool_calls,
                                    { ...data, result: null },
                                ],
                            };
                        }
                        if (event === 'tool_result') {
                            const hasCall = trace.tool_calls.some((call) => call.call_id === data.call_id);
                            return {
                                ...trace,
                                stage: data.error ? `${data.name || 'tool'} failed` : `${data.name || 'tool'} completed`,
                                tool_calls: hasCall
                                    ? trace.tool_calls.map((call) => (
                                        call.call_id === data.call_id ? { ...call, result: data } : call
                                    ))
                                    : [...trace.tool_calls, { ...data, arguments: null, result: data }],
                            };
                        }
                        if (event === 'delta')
                            return { ...trace, stage: 'responding', answer: `${trace.answer}${data.content || ''}` };
                        if (event === 'complete') {
                            return {
                                ...trace,
                                status: 'completed',
                                stage: 'complete',
                                answer: data.answer || trace.answer,
                            };
                        }
                        if (event === 'clarification') {
                            return {
                                ...trace,
                                status: 'clarification',
                                stage: 'clarification needed',
                            };
                        }
                        if (event === 'error') {
                            return {
                                ...trace,
                                status: 'failed',
                                stage: 'failed',
                                error: data.message || 'RevJet AI request failed',
                            };
                        }
                        return trace;
                    }));
                    if (data.thread_id) {
                        resolvedThreadId = data.thread_id;
                        activeThreadRef.current = data.thread_id;
                        setActiveThreadId(data.thread_id);
                    }
                    if (event === 'status')
                        setStatus(data.stage === 'thinking' ? 'Thinking' : data.stage);
                    if (event === 'tool_call') {
                        if (data.name === 'run_aggregation')
                            producedQueryRef.current = true;
                        setMessages((current) => current.map((message) => (
                            message.message_id === assistantId
                                ? {
                                    ...message,
                                    tool_calls: [
                                        ...(message.tool_calls || []),
                                        { ...data, result: null },
                                    ],
                                }
                                : message
                        )));
                    }
                    if (event === 'tool_result') {
                        setMessages((current) => current.map((message) => {
                            if (message.message_id !== assistantId)
                                return message;
                            const calls = message.tool_calls || [];
                            const hasCall = calls.some((call) => call.call_id === data.call_id);
                            return {
                                ...message,
                                tool_calls: hasCall
                                    ? calls.map((call) => (
                                        call.call_id === data.call_id ? { ...call, result: data } : call
                                    ))
                                    : [...calls, { ...data, result: data }],
                            };
                        }));
                    }
                    if (event === 'delta') {
                        setStatus('Responding');
                        setMessages((current) => current.map((message) => (
                            message.message_id === assistantId
                                ? { ...message, content: `${message.content}${data.content || ''}`, loading: true }
                                : message
                        )));
                    }
                    if (event === 'complete') {
                        setMessages((current) => {
                            const nextMessages = current.map((message) => (
                                message.message_id === assistantId
                                ? { ...message, content: data.answer || message.content, loading: false }
                                : message
                            ));
                            if (resolvedThreadId)
                                threadCacheRef.current.set(resolvedThreadId, {
                                    messages: nextMessages,
                                    loadedAt: Date.now(),
                                });
                            return nextMessages;
                        });
                        setObservabilityRefreshKey((current) => current + 1);
                        // A query was produced — gate further input so the
                        // conversation stays grounded to this intent.
                        if (producedQueryRef.current)
                            setPhase('choice');
                    }
                    if (event === 'clarification') {
                        const slots = data.slots || [];
                        setMessages((current) => current.map((message) => (
                            message.message_id === assistantId
                                ? { ...message, loading: false, clarification: slots }
                                : message
                        )));
                        setStatus(null);
                        setObservabilityRefreshKey((current) => current + 1);
                    }
                    if (event === 'suggestions') {
                        const list = data.suggestions || [];
                        setMessages((current) => current.map((message) => (
                            message.message_id === assistantId
                                ? { ...message, suggestions: list }
                                : message
                        )));
                    }
                    if (event === 'error') {
                        streamError = data.message || 'RevJet AI could not answer that question';
                        setMessages((current) => current.map((message) => (
                            message.message_id === assistantId
                                ? { ...message, loading: false, error: streamError }
                                : message
                        )));
                    }
                },
                controller.signal,
            );

            loadThreads().catch(() => {});
        }
        catch (err) {
            setTraces((current) => current.map((trace) => (
                trace.trace_id === traceId
                    ? {
                        ...trace,
                        status: err.name === 'AbortError' ? 'cancelled' : 'failed',
                        stage: err.name === 'AbortError' ? 'cancelled' : 'failed',
                        error: err.name === 'AbortError'
                            ? 'The request was cancelled.'
                            : err.message || 'RevJet AI could not answer that question',
                    }
                    : trace
            )));
            if (err.name !== 'AbortError') {
                const message = err.message || 'RevJet AI could not answer that question';
                setError(message);
                setMessages((current) => current.map((item) => (
                    item.message_id === assistantId
                        ? { ...item, loading: false, error: message }
                        : item
                )));
            }
        }
        finally {
            setSending(false);
            setStatus(null);
            abortRef.current = null;
        }
    };

    return (
        <PageShell>
            <PageHeader
                title="AI Reporting"
                actions={(
                    <>
                        <Button
                            variant={view === 'chat' ? 'contained' : 'outlined'}
                            startIcon={<ChatIcon />}
                            onClick={() => setView('chat')}
                        >
                            Chat
                        </Button>
                        <Button
                            variant={view === 'observability' ? 'contained' : 'outlined'}
                            startIcon={<BugReportIcon />}
                            onClick={() => setView('observability')}
                        >
                            Observability (Dev)
                        </Button>
                        {view === 'chat' && (
                            <Button startIcon={<AddCommentIcon />} onClick={newConversation}>
                                New conversation
                            </Button>
                        )}
                    </>
                )}
            />
            <PageContent fill sx={{ p: { xs: 1, md: 2 } }}>
                <Box
                    aria-hidden={view !== 'observability'}
                    sx={{
                        display: view === 'observability' ? 'flex' : 'none',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <AIObservability
                        active={view === 'observability'}
                        liveTraces={traces}
                        refreshKey={observabilityRefreshKey}
                    />
                </Box>
                <Box
                    aria-hidden={view !== 'chat'}
                    sx={{
                        display: view === 'chat' ? 'flex' : 'none',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                    <Box
                        sx={{
                            width: { xs: '100%', md: 280 },
                            maxHeight: { xs: 190, md: 'none' },
                            overflowY: 'auto',
                            flexShrink: 0,
                            borderRight: { md: 1 },
                            borderBottom: { xs: 1, md: 0 },
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="overline" color="text.secondary">
                                Conversations
                            </Typography>
                        </Box>
                        <Divider />
                        <List dense disablePadding>
                            {threads.map((thread) => (
                                <ListItemButton
                                    key={thread.thread_id}
                                    selected={thread.thread_id === activeThreadId}
                                    disabled={sending}
                                    onClick={() => openThread(thread.thread_id).catch((err) => setError(err.message))}
                                >
                                    <ListItemText
                                        primary={thread.title}
                                        secondary={(
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {thread.updated_at ? new Date(thread.updated_at).toLocaleString() : ''}
                                                </Typography>
                                                {thread.needs_clarification && (
                                                    <Chip size="small" color="warning" label="clarification" sx={{ height: 18, fontSize: 10 }} />
                                                )}
                                            </Stack>
                                        )}
                                        primaryTypographyProps={{ noWrap: true }}
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
                        <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>
                            <Stack spacing={1.5} sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
                                {error && <Alert severity="error">{error}</Alert>}
                                {messages.length === 0 && (
                                    <Box sx={{ maxWidth: 680, mx: 'auto', py: { xs: 3, md: 8 }, textAlign: 'center' }}>
                                        <AutoAwesomeIcon color="primary" sx={{ fontSize: 42 }} />
                                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 600 }}>
                                            Ask RevJet AI about reporting
                                        </Typography>
                                        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
                                            Start with the report you need, the time period, and any important filters.
                                        </Typography>
                                        <Stack direction="row" useFlexGap flexWrap="wrap" justifyContent="center" spacing={1} sx={{ mt: 3 }}>
                                            {suggestions.map((suggestion) => (
                                                <Chip
                                                    key={suggestion}
                                                    label={suggestion}
                                                    variant="outlined"
                                                    onClick={() => sendQuestion(suggestion)}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                                {messages.map((message) => (
                                    <MessageBubble
                                        key={message.message_id}
                                        message={message}
                                        onClarify={(candidate) => sendQuestion(candidate, { injected: true })}
                                        onSuggest={(msg) => sendQuestion(msg, { injected: true })}
                                        actionsDisabled={sending}
                                    />
                                ))}
                                {phase === 'choice' && (
                                    <CompletionPrompt
                                        disabled={sending}
                                        onContinue={() => setPhase('active')}
                                        onNew={newConversation}
                                        onEnd={() => setPhase('ended')}
                                    />
                                )}
                                {phase === 'ended' && (
                                    <Alert
                                        severity="info"
                                        sx={{ maxWidth: 640, mx: 'auto' }}
                                        action={(
                                            <Button color="inherit" size="small" startIcon={<AddCommentIcon />} onClick={newConversation}>
                                                New
                                            </Button>
                                        )}
                                    >
                                        This conversation has ended. Start a new conversation to ask something else.
                                    </Alert>
                                )}
                            </Stack>
                        </Box>

                        <Divider />
                        <Box sx={{ px: { xs: 1.5, md: 3 }, py: 1.5, maxWidth: 900, mx: 'auto', width: '100%' }}>
                            {status && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                    {status}...
                                </Typography>
                            )}
                            <Stack direction="row" spacing={1} alignItems="flex-end">
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    value={draft}
                                    disabled={sending || phase !== 'active'}
                                    placeholder={
                                        phase === 'choice'
                                            ? 'Choose continue, new, or end above'
                                            : phase === 'ended'
                                                ? 'This conversation has ended'
                                                : 'Ask a reporting question'
                                    }
                                    onChange={(event) => setDraft(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' && !event.shiftKey) {
                                            event.preventDefault();
                                            sendQuestion();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    disabled={sending || phase !== 'active' || draft.trim().length < 3}
                                    onClick={() => sendQuestion()}
                                    sx={{ minWidth: 48, height: 48 }}
                                    aria-label="Send question"
                                >
                                    {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                    </Paper>
                </Box>
            </PageContent>
        </PageShell>
    );
}
